import { defineSettingsScreen } from '@heidi/runtime/shell';

/**
 * The `/settings/prechart` screen body.
 *
 * `id` is the settings route slug (`ROUTE_TO_SLUG['/settings/prechart']`),
 * which is what lets a package serve an existing settings screen with the
 * sidebar and the IA untouched: the loader resolves the route to this
 * contribution and falls back to the app's own importer for every route no
 * package claims. An id that is not a slug is silently skipped, so the value
 * here is measured rather than named after the screen.
 *
 * `section: 'personal'` is measured, not chosen — the screen renders
 * `group={intl.formatMessage({ defaultMessage: 'Personal' })}` in its header,
 * and `personal` is the first group in `settingsSlotMap`.
 *
 * `order: 0` inside the section's `[0, 999]` band. It is inert today — screens
 * are served by id and the sidebar is hand-written — so a low value claims no
 * position anyone else has to renumber around. This screen has no sidebar item
 * at all: it is reached by `?settings=prechart` and by the standalone
 * `/settings/prechart` page, which makes the value inert twice over.
 *
 * `builds: 'all'`: measured. Nothing in the screen or in the three delegates it
 * reads consults the build profile — no `isOnPrem`, no `isTauri`, no
 * `isAppleCompliantBuild` — and the route is registered unconditionally in
 * `ROUTE_TO_SLUG` and `rawScreenImporters` on every profile, so a narrower
 * value would withhold from the on-prem shell a screen the appliance resolves
 * today. The internal-admin stats panel inside the screen is gated by a flag,
 * not by a build.
 */
export const prechartSettingsScreen = defineSettingsScreen({
  id: 'prechart',
  section: 'personal',
  order: 0,
  // The thunk names the export rather than relying on a default one: the screen
  // is a named export like every other component in this package, and
  // `ContributionLoadThunk` wants `{ default }`. It points at the SCREEN MODULE
  // and never at the `.` door, which carries the whole domain.
  load: () =>
    import('../ui/settings/PrechartSettings').then((module) => ({
      default: module.PrechartSettings,
    })),
  builds: 'all',
});
